import { useState } from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipLoader } from "react-spinners";
import { updateUser } from "../../services/userService";
import axios from "axios";
import { useAppDispatch } from "../../data/store/hooks";
import { loadUser } from "../../data/store/reducers/userSlice";

interface UploadImageProps {
  userImage?: string;
  userData: any;
}

const UploadImage: React.FC<UploadImageProps> = ({ userImage, userData }) => {
  const [image, setImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const saveImage = useMutation({
    mutationFn: async (newImage: string) => {
      const updatedUser = { ...userData, image: newImage };
      return updateUser(updatedUser);
    },
    onSuccess: async () => {
      toast.success("Profile updated!");
      await dispatch(loadUser()); // Ensure loadUser is properly dispatched
      queryClient.invalidateQueries({ queryKey: ["loggedInUser"] });
    },
    onError: () => {
      toast.error("Failed to update profile image.");
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "talent527");

    setIsUploading(true);
    try {
      const res = await axios.post(
        "https://api.cloudinary.com/v1_1/kencodin/image/upload",
        formData,
        {
          withCredentials: false,
        }
      );
      const uploadedImage = res.data.secure_url;
      setImage(uploadedImage);
      toast.success("Image uploaded!");

      // Save the image after successful upload
      saveImage.mutate(uploadedImage);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Image upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col space-y-1 w-[120px]">
      <div className="w-[120px] h-[150px] rounded-xl overflow-hidden">
        {image || userImage !== "https://i.ibb.co/4pDNDk1/avatar.png" ? (
          <img
            src={image || userImage}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-rose-500 flex items-center justify-center text-white font-bold text-5xl">
            A
          </div>
        )}
      </div>

      <input
        type="file"
        accept="image/*"
        className="hidden"
        id="upload-btn"
        onChange={handleImageUpload}
      />
      <label
        htmlFor="upload-btn"
        className="border p-2 rounded-lg text-sm cursor-pointer text-center"
      >
        {isUploading ? <ClipLoader size={16} /> : "Upload"}
      </label>
    </div>
  );
};

export default UploadImage;
